import { getHighestSafeWindowContext, getWindowHeight, getWindowWidth } from 'components/image/imageRenderingUtils';
import {
  ACTION_MOVE,
  ACTION_NONE,
  ACTION_PINCH,
  ACTION_SWIPE,
  KEYS,
  MAX_ZOOM_LEVEL,
  MIN_SWIPE_DISTANCE,
  MIN_ZOOM_LEVEL,
  SOURCE_ANY,
  SOURCE_MOUSE,
  SOURCE_POINTER,
  SOURCE_TOUCH,
  WHEEL_MOVE_X_THRESHOLD,
  WHEEL_MOVE_Y_THRESHOLD,
  ZOOM_BUTTON_INCREMENT_SIZE,
  ZOOM_RATIO
} from 'components/image/imageZoomUtils';
import _ from 'lodash';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Icon, Loader } from 'semantic-ui-react';
import styled from 'styled-components';

interface Props extends MessageSourceProps {
  mainSrc: string;
  prevSrc: string;
  nextSrc: string;
  mainSrcThumbnail: string;
  prevSrcThumbnail: string;
  nextSrcThumbnail: string;
  onMovePrevRequest: (event: any) => void;
  onMoveNextRequest: (event: any) => void;
  onCloseRequest?: (event: any) => void;
  onAfterOpen: () => void;
  imageTitle: React.ReactNode;
  imageCaption: React.ReactNode;
  toolbarButtons: Array<React.ReactNode>;
  segmentWidth: number;
  segmentHeight: number;
}

interface State {
  zoomLevel: number,
  offsetX: number,
  offsetY: number,
  shouldAnimate: boolean,
  loadErrorStatus: any
}

const navigateButtonStyle = {
  border: "none",
  position: "absolute",
  top: 0,
  bottom: 0,
  width: "20px",
  height: "34px",
  margin: "auto",
  cursor: "pointer",
  background: "transparent"
}

const StyledZoomOptionsButton = styled(Button)`
  &.ui.inverted.button.styled-button {
    box-shadow: none !important;
    opacity: 0.7;
    background-color: transparent !important;
  }

  &.ui.inverted.button:hover,
  &.ui.inverted.button:active,
  &.ui.inverted.button:focus {
    opacity: 1;
    color: #FFFFFF !important;
    background-color: transparent !important;
  }
`

class ImageComponent extends Component<Props, State> {

  private windowContext: any;
  private listeners: any;
  private didUnmount: boolean = false;

  static defaultProps = {
    imageCaption: null,
    mainSrcThumbnail: null,
    nextSrcThumbnail: null,
    prevSrcThumbnail: null
  }

  static isTargetMatchImage(target: any) {
    return target && /ril-image-current/.test(target.className);
  }

  static parseMouseEvent(mouseEvent: any) {
    return {
      id: 'mouse',
      source: SOURCE_MOUSE,
      x: parseInt(mouseEvent.clientX, 10),
      y: parseInt(mouseEvent.clientY, 10)
    };
  }

  static parseTouchPointer(touchPointer: any) {
    return {
      id: touchPointer.identifier,
      source: SOURCE_TOUCH,
      x: parseInt(touchPointer.clientX, 10),
      y: parseInt(touchPointer.clientY, 10)
    };
  }

  static parsePointerEvent(pointerEvent: any) {
    return {
      id: pointerEvent.pointerId,
      source: SOURCE_POINTER,
      x: parseInt(pointerEvent.clientX, 10),
      y: parseInt(pointerEvent.clientY, 10)
    };
  }

  // @ts-ignore
  static getTransform(segmentWidth: number, {x = 0, y = 0, zoom = 1, width, targetWidth}) {
    let nextX = x;
    const divWidth = segmentWidth;

    if (width > divWidth) {
      nextX += (divWidth - width) / 2;
    }
    const scaleFactor = zoom * (targetWidth / width);

    return {
      transform: `translate3d(${nextX}px,${y}px,0) scale3d(${scaleFactor},${scaleFactor},1)`
    };
  }

  outerEl: React.RefObject<any> = React.createRef();
  zoomInBtn: React.RefObject<any> = React.createRef();
  defaultImgView: React.RefObject<any> = React.createRef();
  zoomOutBtn: React.RefObject<any> = React.createRef();
  caption: React.RefObject<any> = React.createRef();

  timeouts: number[] = [];
  currentAction: number = ACTION_NONE;

  eventsSource: number = SOURCE_ANY;

  pointerList: any[] = [];

  preventInnerClose: boolean = false;
  preventInnerCloseTimeout: number | null = null;

  keyPressed: boolean = false;

  imageCache: any = {};

  lastKeyDownTime: number = 0;

  resizeTimeout: number | null = null;

  wheelActionTimeout: number | null = null;
  resetScrollTimeout: number | null = null;
  scrollX: number = 0;
  scrollY: number = 0;

  moveStartX: number = 0;
  moveStartY: number = 0;
  moveStartOffsetX: number = 0;
  moveStartOffsetY: number = 0;

  swipeStartX: number = 0;
  swipeStartY: number = 0;
  swipeEndX: number = 0;
  swipeEndY: number = 0;

  pinchTouchList: any = null;
  pinchDistance: number = 0;

  keyCounter: number = 0;

  moveRequested: boolean = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      zoomLevel: MIN_ZOOM_LEVEL,
      offsetX: 0,
      offsetY: 0,
      shouldAnimate: false,
      loadErrorStatus: {}
    };

    this.handleImageDoubleClick = this.handleImageDoubleClick.bind(this);
    this.handleImageMouseWheel = this.handleImageMouseWheel.bind(this);
    this.handleKeyInput = this.handleKeyInput.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleOuterMousewheel = this.handleOuterMousewheel.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handlePointerEvent = this.handlePointerEvent.bind(this);
    this.handleCaptionMousewheel = this.handleCaptionMousewheel.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleZoomInButtonClick = this.handleZoomInButtonClick.bind(this);
    this.handleDefaultViewButtonClick = this.handleDefaultViewButtonClick.bind(this);
    this.handleZoomOutButtonClick = this.handleZoomOutButtonClick.bind(this);
    this.requestMoveNext = this.requestMoveNext.bind(this);
    this.requestMovePrev = this.requestMovePrev.bind(this);
  }

  componentDidMount() {

    this.windowContext = getHighestSafeWindowContext();

    this.listeners = {
      resize: this.handleWindowResize,
      mouseup: this.handleMouseUp,
      touchend: this.handleTouchEnd,
      touchcancel: this.handleTouchEnd,
      pointerdown: this.handlePointerEvent,
      pointermove: this.handlePointerEvent,
      pointerup: this.handlePointerEvent,
      pointercancel: this.handlePointerEvent
    };

    Object.keys(this.listeners).forEach(type => {
      this.windowContext.addEventListener(type, this.listeners[type]);
    });

    this.loadAllImages();
  }

  shouldComponentUpdate = (nextProps: any) => {

    this.getSrcTypes().forEach((srcType: any) => {
      const props: any = this.props;

      if (props[srcType.name] !== nextProps[srcType.name]) {
        this.moveRequested = false;
      }
    });

    return !this.moveRequested;
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    let sourcesChanged = false;
    const prevSrcDict = {};
    const nextSrcDict = {};

    this.getSrcTypes().forEach((srcType: any) => {
      const previousProps: any = prevProps;
      const props: any = this.props;

      if (previousProps[srcType.name] !== props[srcType.name]) {
        sourcesChanged = true;

        (prevSrcDict as any)[(prevProps as any)[srcType.name]] = true;
        (nextSrcDict as any)[props[srcType.name]] = true;
        this.setState({
          zoomLevel: MIN_ZOOM_LEVEL,
          offsetX: 0,
          offsetY: 0
        })
      }
    });

    if (sourcesChanged || this.moveRequested) {
      Object.keys(prevSrcDict).forEach(prevSrc => {
        if (!(prevSrc in nextSrcDict) && prevSrc in this.imageCache) {
          this.imageCache[prevSrc].loaded = false;
        }
      });

      this.moveRequested = false;

      this.loadAllImages(this.props);
    }
  }

  componentWillUnmount() {
    this.didUnmount = true;

    Object.keys(this.listeners).forEach(type => {
      this.windowContext.removeEventListener(type, this.listeners[type]);
    });

    this.timeouts.forEach(tid => clearTimeout(tid));
  }

  setTimeout(func: any, time: number) {
    const id: number = setTimeout(() => {
      this.timeouts = this.timeouts.filter(tid => tid !== id);
      func();
    }, time);

    this.timeouts.push(id);

    return id;
  }

  setPreventInnerClose() {
    if (this.preventInnerCloseTimeout) {
      this.clearTimeout(this.preventInnerCloseTimeout);
    }
    this.preventInnerClose = true;
    this.preventInnerCloseTimeout = this.setTimeout(() => {
      this.preventInnerClose = false;
      this.preventInnerCloseTimeout = null;
    }, 100);
  }

  getBestImageForType(srcType: string) {
    const props: any = this.props;
    let imageSrc = props[srcType];
    let fitSizes: any = {};

    if (this.isImageLoaded(imageSrc)) {
      fitSizes = this.getFitSizes(
          this.imageCache[imageSrc].width,
          this.imageCache[imageSrc].height,
          false
      );
    } else if (this.isImageLoaded(props[`${srcType}Thumbnail`])) {
      imageSrc = props[`${srcType}Thumbnail`];
      fitSizes = this.getFitSizes(
          this.imageCache[imageSrc].width,
          this.imageCache[imageSrc].height,
          true
      );
    } else {
      return null;
    }

    return {
      src: imageSrc,
      height: this.imageCache[imageSrc].height,
      width: this.imageCache[imageSrc].width,
      targetHeight: fitSizes.height,
      targetWidth: fitSizes.width
    };
  }

  getFitSizes(width: number, height: number, stretch: boolean) {
    const boxSize = this.getLightboxRect();
    let maxHeight = boxSize.height - 20;
    let maxWidth = boxSize.width - 20;

    if (!stretch) {
      maxHeight = Math.min(maxHeight, height);
      maxWidth = Math.min(maxWidth, width);
    }

    const maxRatio = maxWidth / maxHeight;
    const srcRatio = width / height;

    if (maxRatio > srcRatio) {
      return {
        width: (width * maxHeight) / height,
        height: maxHeight
      };
    }

    return {
      width: maxWidth,
      height: (height * maxWidth) / width
    };
  }

  getMaxOffsets(zoomLevel = this.state.zoomLevel) {
    const currentImageInfo = this.getBestImageForType('mainSrc');
    if (currentImageInfo === null) {
      return {maxX: 0, minX: 0, maxY: 0, minY: 0};
    }

    const boxSize = this.getLightboxRect();
    const zoomMultiplier = this.getZoomMultiplier(zoomLevel);

    let maxX = 0;
    if (zoomMultiplier * currentImageInfo.width - boxSize.width < 0) {
      maxX = (boxSize.width - zoomMultiplier * currentImageInfo.width) / 2;
    } else {
      maxX = (zoomMultiplier * currentImageInfo.width - boxSize.width) / 2;
    }

    let maxY = 0;
    if (zoomMultiplier * currentImageInfo.height - boxSize.height < 0) {
      maxY = (boxSize.height - zoomMultiplier * currentImageInfo.height) / 2;
    } else {
      maxY = (zoomMultiplier * currentImageInfo.height - boxSize.height) / 2;
    }

    return {
      maxX,
      maxY,
      minX: -1 * maxX,
      minY: -1 * maxY
    };
  }

  getSrcTypes() {
    return [
      {
        name: 'mainSrc',
        keyEnding: `i${this.keyCounter}`
      },
      {
        name: 'mainSrcThumbnail',
        keyEnding: `t${this.keyCounter}`
      },
      {
        name: 'nextSrc',
        keyEnding: `i${this.keyCounter + 1}`
      },
      {
        name: 'nextSrcThumbnail',
        keyEnding: `t${this.keyCounter + 1}`
      },
      {
        name: 'prevSrc',
        keyEnding: `i${this.keyCounter - 1}`
      },
      {
        name: 'prevSrcThumbnail',
        keyEnding: `t${this.keyCounter - 1}`
      }
    ];
  }

  getZoomMultiplier(zoomLevel = this.state.zoomLevel) {
    return ZOOM_RATIO ** zoomLevel;
  }

  getLightboxRect() {

    if (this.outerEl.current) {
      return this.outerEl.current.getBoundingClientRect();
    }

    const {segmentWidth, segmentHeight} = this.props;

    return {
      width: !_.isNil(segmentWidth) ? segmentWidth : getWindowWidth(),
      height: !_.isNil(segmentHeight) ? segmentHeight : getWindowHeight(),
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    };
  }

  clearTimeout(id: number | null) {
    this.timeouts = this.timeouts.filter(tid => tid !== id);
    clearTimeout(id!);
  }

  changeZoom(zoomLevel: number, clientX?: number, clientY?: number) {
    const nextZoomLevel = Math.max(
        MIN_ZOOM_LEVEL,
        Math.min(MAX_ZOOM_LEVEL, zoomLevel)
    );

    if (nextZoomLevel === this.state.zoomLevel) {
      return;
    }

    if (nextZoomLevel === MIN_ZOOM_LEVEL) {
      this.setState({
        zoomLevel: nextZoomLevel,
        offsetX: 0,
        offsetY: 0
      });

      return;
    }

    const imageBaseSize = this.getBestImageForType('mainSrc');
    if (imageBaseSize === null) {
      return;
    }

    const currentZoomMultiplier = this.getZoomMultiplier();
    const nextZoomMultiplier = this.getZoomMultiplier(nextZoomLevel);

    const boxRect = this.getLightboxRect();
    const pointerX =
        clientX !== undefined
            ? clientX - boxRect.left
            : boxRect.width / 2;
    const pointerY =
        clientY !== undefined
            ? clientY - boxRect.top
            : boxRect.height / 2;

    const currentImageOffsetX =
        (boxRect.width - imageBaseSize.width * currentZoomMultiplier) / 2;
    const currentImageOffsetY =
        (boxRect.height - imageBaseSize.height * currentZoomMultiplier) / 2;

    const currentImageRealOffsetX = currentImageOffsetX - this.state.offsetX;
    const currentImageRealOffsetY = currentImageOffsetY - this.state.offsetY;

    const currentPointerXRelativeToImage =
        (pointerX - currentImageRealOffsetX) / currentZoomMultiplier;
    const currentPointerYRelativeToImage =
        (pointerY - currentImageRealOffsetY) / currentZoomMultiplier;

    const nextImageRealOffsetX =
        pointerX - currentPointerXRelativeToImage * nextZoomMultiplier;
    const nextImageRealOffsetY =
        pointerY - currentPointerYRelativeToImage * nextZoomMultiplier;

    const nextImageOffsetX =
        (boxRect.width - imageBaseSize.width * nextZoomMultiplier) / 2;
    const nextImageOffsetY =
        (boxRect.height - imageBaseSize.height * nextZoomMultiplier) / 2;

    let nextOffsetX = nextImageOffsetX - nextImageRealOffsetX;
    let nextOffsetY = nextImageOffsetY - nextImageRealOffsetY;

    if (this.currentAction !== ACTION_PINCH) {
      const maxOffsets = this.getMaxOffsets();
      if (this.state.zoomLevel > nextZoomLevel) {
        nextOffsetX = Math.max(
            maxOffsets.minX,
            Math.min(maxOffsets.maxX, nextOffsetX)
        );
        nextOffsetY = Math.max(
            maxOffsets.minY,
            Math.min(maxOffsets.maxY, nextOffsetY)
        );
      }
    }

    this.setState({
      zoomLevel: nextZoomLevel,
      offsetX: nextOffsetX,
      offsetY: nextOffsetY
    });
  }

  handleKeyInput(event: any) {
    event.stopPropagation();

    if (this.isAnimating()) {
      return;
    }

    if (event.type === 'keyup') {
      this.lastKeyDownTime -= 40;
      return;
    }

    const keyCode = event.which || event.keyCode;

    const currentTime = new Date();
    if (
        currentTime.getTime() - this.lastKeyDownTime < 180) {
      return;
    }
    this.lastKeyDownTime = currentTime.getTime();

    switch (keyCode) {
      case KEYS.LEFT_ARROW:
        if (!this.props.prevSrc) {
          return;
        }

        event.preventDefault();
        this.keyPressed = true;
        this.requestMovePrev(event);
        break;

      case KEYS.RIGHT_ARROW:
        if (!this.props.nextSrc) {
          return;
        }

        event.preventDefault();
        this.keyPressed = true;
        this.requestMoveNext(event);
        break;

      default:
    }
  }

  handleOuterMousewheel(event: any) {
    event.stopPropagation();

    const xThreshold = WHEEL_MOVE_X_THRESHOLD;
    let actionDelay = 0;
    const imageMoveDelay = 500;

    this.clearTimeout(this.resetScrollTimeout!);
    this.resetScrollTimeout = this.setTimeout(() => {
      this.scrollX = 0;
      this.scrollY = 0;
    }, 300);

    if (this.wheelActionTimeout !== null || this.isAnimating()) {
      return;
    }

    if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) {
      this.scrollY = 0;
      this.scrollX += event.deltaX;

      const bigLeapX = xThreshold / 2;
      if (this.scrollX >= xThreshold || event.deltaX >= bigLeapX) {
        this.requestMoveNext(event);
        actionDelay = imageMoveDelay;
        this.scrollX = 0;
      } else if (
          this.scrollX <= -1 * xThreshold ||
          event.deltaX <= -1 * bigLeapX
      ) {
        this.requestMovePrev(event);
        actionDelay = imageMoveDelay;
        this.scrollX = 0;
      }
    }

    if (actionDelay !== 0) {
      this.wheelActionTimeout = this.setTimeout(() => {
        this.wheelActionTimeout = null;
      }, actionDelay);
    }
  }

  handleImageMouseWheel(event: any) {
    if (Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
      event.stopPropagation();
      if (Math.abs(event.deltaY) < WHEEL_MOVE_Y_THRESHOLD) {
        return;
      }

      this.scrollX = 0;
      this.scrollY += event.deltaY;

      this.changeZoom(
          this.state.zoomLevel - event.deltaY,
          event.clientX,
          event.clientY
      );
    }
  }

  handleImageDoubleClick(event: any) {
    if (this.state.zoomLevel > MIN_ZOOM_LEVEL) {
      this.changeZoom(MIN_ZOOM_LEVEL, event.clientX, event.clientY);
    } else {
      this.changeZoom(
          this.state.zoomLevel + ZOOM_BUTTON_INCREMENT_SIZE,
          event.clientX,
          event.clientY
      );
    }
  }

  shouldHandleEvent(source: number) {
    if (this.eventsSource === source) {
      return true;
    }
    if (this.eventsSource === SOURCE_ANY) {
      this.eventsSource = source;
      return true;
    }
    switch (source) {
      case SOURCE_MOUSE:
        return false;
      case SOURCE_TOUCH:
        this.eventsSource = SOURCE_TOUCH;
        this.filterPointersBySource();
        return true;
      case SOURCE_POINTER:
        if (this.eventsSource === SOURCE_MOUSE) {
          this.eventsSource = SOURCE_POINTER;
          this.filterPointersBySource();
          return true;
        }
        return false;
      default:
        return false;
    }
  }

  addPointer(pointer: any) {
    this.pointerList.push(pointer);
  }

  removePointer(pointer: any) {
    this.pointerList = this.pointerList.filter(({id}) => id !== pointer.id);
  }

  filterPointersBySource() {
    this.pointerList = this.pointerList.filter(
        ({source}) => source === this.eventsSource
    );
  }

  handleMouseDown(event: any) {
    if (
        this.shouldHandleEvent(SOURCE_MOUSE)
        && ImageComponent.isTargetMatchImage(event.target)
    ) {
      this.addPointer(ImageComponent.parseMouseEvent(event));
      this.multiPointerStart(event);
    }
  }

  handleMouseMove(event: any) {
    if (this.shouldHandleEvent(SOURCE_MOUSE)) {
      this.multiPointerMove(event, [ImageComponent.parseMouseEvent(event)]);
    }
  }

  handleMouseUp(event: any) {
    if (this.shouldHandleEvent(SOURCE_MOUSE)) {
      this.removePointer(ImageComponent.parseMouseEvent(event));
      this.multiPointerEnd(event);
    }
  }

  handlePointerEvent(event: any) {
    if (this.shouldHandleEvent(SOURCE_POINTER)) {
      switch (event.type) {
        case 'pointerdown':
          if (ImageComponent.isTargetMatchImage(event.target)) {
            this.addPointer(ImageComponent.parsePointerEvent(event));
            this.multiPointerStart(event);
          }
          break;
        case 'pointermove':
          this.multiPointerMove(event, [
            ImageComponent.parsePointerEvent(event)
          ]);
          break;
        case 'pointerup':
        case 'pointercancel':
          this.removePointer(ImageComponent.parsePointerEvent(event));
          this.multiPointerEnd(event);
          break;
        default:
          break;
      }
    }
  }

  handleTouchStart(event: any) {
    if (this.shouldHandleEvent(SOURCE_TOUCH)
        && ImageComponent.isTargetMatchImage(event.target)) {
      [].forEach.call(event.changedTouches, (eventTouch: any) =>
          this.addPointer(ImageComponent.parseTouchPointer(eventTouch))
      );
      this.multiPointerStart(event);
    }
  }

  handleTouchMove(event: any) {
    if (this.shouldHandleEvent(SOURCE_TOUCH)) {
      this.multiPointerMove(
          event,
          [].map.call(event.changedTouches, (eventTouch: any) =>
              ImageComponent.parseTouchPointer(eventTouch)
          )
      );
    }
  }

  handleTouchEnd(event: any) {
    if (this.shouldHandleEvent(SOURCE_TOUCH)) {
      [].map.call(event.changedTouches, (touch: any): any => {
        this.removePointer(ImageComponent.parseTouchPointer(touch))
      });

      this.multiPointerEnd(event);
    }
  }

  decideMoveOrSwipe(pointer: any) {
    if (this.state.zoomLevel <= MIN_ZOOM_LEVEL) {
      this.handleSwipeStart(pointer);
    } else {
      this.handleMoveStart(pointer);
    }
  }

  multiPointerStart(event: any) {
    this.handleEnd(null);
    switch (this.pointerList.length) {
      case 1: {
        event.preventDefault();
        this.decideMoveOrSwipe(this.pointerList[0]);
        break;
      }
      case 2: {
        event.preventDefault();
        this.handlePinchStart(this.pointerList);
        break;
      }
      default:
        break;
    }
  }

  multiPointerMove(event: any, pointerList: any) {
    switch (this.currentAction) {
      case ACTION_MOVE: {
        event.preventDefault();
        this.handleMove(pointerList[0]);
        break;
      }
      case ACTION_SWIPE: {
        event.preventDefault();
        this.handleSwipe(pointerList[0]);
        break;
      }
      case ACTION_PINCH: {
        event.preventDefault();
        this.handlePinch(pointerList);
        break;
      }
      default:
        break;
    }
  }

  multiPointerEnd(event: any) {
    if (this.currentAction !== ACTION_NONE) {
      this.setPreventInnerClose();
      this.handleEnd(event);
    }
    switch (this.pointerList.length) {
      case 0: {
        this.eventsSource = SOURCE_ANY;
        break;
      }
      case 1: {
        event.preventDefault();
        this.decideMoveOrSwipe(this.pointerList[0]);
        break;
      }
      case 2: {
        event.preventDefault();
        this.handlePinchStart(this.pointerList);
        break;
      }
      default:
        break;
    }
  }

  handleEnd(event: any) {
    switch (this.currentAction) {
      case ACTION_MOVE:
        this.handleMoveEnd();
        break;
      case ACTION_SWIPE:
        this.handleSwipeEnd(event);
        break;
      case ACTION_PINCH:
        this.handlePinchEnd();
        break;
      default:
        break;
    }
  }

  handleMoveStart({x: clientX, y: clientY}: any) {
    this.currentAction = ACTION_MOVE;
    this.moveStartX = clientX;
    this.moveStartY = clientY;
    this.moveStartOffsetX = this.state.offsetX;
    this.moveStartOffsetY = this.state.offsetY;
  }

  handleMove({x: clientX, y: clientY}: any) {
    const newOffsetX = this.moveStartX - clientX + this.moveStartOffsetX;
    const newOffsetY = this.moveStartY - clientY + this.moveStartOffsetY;
    if (
        this.state.offsetX !== newOffsetX ||
        this.state.offsetY !== newOffsetY
    ) {
      this.setState({
        offsetX: newOffsetX,
        offsetY: newOffsetY
      });
    }
  }

  handleMoveEnd() {
    this.currentAction = ACTION_NONE;
    this.moveStartX = 0;
    this.moveStartY = 0;
    this.moveStartOffsetX = 0;
    this.moveStartOffsetY = 0;

    const maxOffsets = this.getMaxOffsets();
    const nextOffsetX = Math.max(
        maxOffsets.minX,
        Math.min(maxOffsets.maxX, this.state.offsetX)
    );
    const nextOffsetY = Math.max(
        maxOffsets.minY,
        Math.min(maxOffsets.maxY, this.state.offsetY)
    );
    if (
        nextOffsetX !== this.state.offsetX ||
        nextOffsetY !== this.state.offsetY
    ) {
      this.setState({
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
        shouldAnimate: true
      });

      this.setTimeout(() => {
        this.setState({shouldAnimate: false});
      }, 300);
    }
  }

  handleSwipeStart({x: clientX, y: clientY}: any) {
    this.currentAction = ACTION_SWIPE;
    this.swipeStartX = clientX;
    this.swipeStartY = clientY;
    this.swipeEndX = clientX;
    this.swipeEndY = clientY;
  }

  handleSwipe({x: clientX, y: clientY}: any) {
    this.swipeEndX = clientX;
    this.swipeEndY = clientY;
  }

  handleSwipeEnd(event: any) {
    const {prevSrc, nextSrc} = this.props;

    const xDiff = this.swipeEndX - this.swipeStartX;
    const xDiffAbs = Math.abs(xDiff);
    const yDiffAbs = Math.abs(this.swipeEndY - this.swipeStartY);

    this.currentAction = ACTION_NONE;
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeEndX = 0;
    this.swipeEndY = 0;

    if (!event || this.isAnimating() || xDiffAbs < yDiffAbs * 1.5) {
      return;
    }

    if (xDiffAbs < MIN_SWIPE_DISTANCE) {
      const boxRect = this.getLightboxRect();
      if (xDiffAbs < boxRect.width / 4) {
        return;
      }
    }

    if (xDiff > 0 && prevSrc) {
      event.preventDefault();
      this.requestMovePrev(event);
    } else if (xDiff < 0 && nextSrc) {
      event.preventDefault();
      this.requestMoveNext(event);
    }
  }

  calculatePinchDistance([a, b] = this.pinchTouchList) {
    return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
  }

  calculatePinchCenter([a, b] = this.pinchTouchList) {
    return {
      x: a.x - (a.x - b.x) / 2,
      y: a.y - (a.y - b.y) / 2
    };
  }

  handlePinchStart(pointerList: any) {
    this.currentAction = ACTION_PINCH;
    this.pinchTouchList = pointerList.map(({id, x, y}: any) => ({id, x, y}));
    this.pinchDistance = this.calculatePinchDistance();
  }

  handlePinch(pointerList: any) {
    this.pinchTouchList = this.pinchTouchList.map((oldPointer: any) => {
      for (let i = 0; i < pointerList.length; i += 1) {
        if (pointerList[i].id === oldPointer.id) {
          return pointerList[i];
        }
      }

      return oldPointer;
    });

    const newDistance = this.calculatePinchDistance();

    const zoomLevel = this.state.zoomLevel + newDistance - this.pinchDistance;

    this.pinchDistance = newDistance;
    const {x: clientX, y: clientY} = this.calculatePinchCenter(
        this.pinchTouchList
    );

    this.changeZoom(zoomLevel, clientX, clientY);
  }

  handlePinchEnd() {
    this.currentAction = ACTION_NONE;
    this.pinchTouchList = null;
    this.pinchDistance = 0;
  }

  handleWindowResize() {
    this.clearTimeout(this.resizeTimeout);
    this.resizeTimeout = this.setTimeout(this.forceUpdate.bind(this), 100);
  }

  handleZoomInButtonClick() {
    const nextZoomLevel = this.state.zoomLevel + ZOOM_BUTTON_INCREMENT_SIZE;
    this.changeZoom(nextZoomLevel);
    if (nextZoomLevel === MAX_ZOOM_LEVEL) {
      this.zoomOutBtn.current.focus();
    }
  }

  handleDefaultViewButtonClick() {
    this.changeZoom(MIN_ZOOM_LEVEL);
    this.zoomInBtn.current.focus();
  }

  handleZoomOutButtonClick() {
    const nextZoomLevel = this.state.zoomLevel - ZOOM_BUTTON_INCREMENT_SIZE;
    this.changeZoom(nextZoomLevel);
    if (nextZoomLevel === MIN_ZOOM_LEVEL) {
      this.zoomInBtn.current.focus();
    }
  }

  handleCaptionMousewheel(event: any) {
    event.stopPropagation();

    if (!this.caption.current) {
      return;
    }

    const {height} = this.caption.current.getBoundingClientRect();
    const {scrollHeight, scrollTop} = this.caption.current;
    if (
        (event.deltaY > 0 && height + scrollTop >= scrollHeight) ||
        (event.deltaY < 0 && scrollTop <= 0)
    ) {
      event.preventDefault();
    }
  }

  isAnimating() {
    return this.state.shouldAnimate;
  }

  isImageLoaded(imageSrc: string) {
    return (
        imageSrc &&
        imageSrc in this.imageCache &&
        this.imageCache[imageSrc].loaded
    );
  }

  loadImage(srcType: string, imageSrc: string, done: any) {
    if (this.isImageLoaded(imageSrc)) {
      this.setTimeout(() => {
        done();
      }, 1);
      return;
    }

    const inMemoryImage = new Image();

    inMemoryImage.onerror = errorEvent => {
      this.setState(prevState => ({
        loadErrorStatus: {...prevState.loadErrorStatus, [srcType]: true}
      }));

      done(errorEvent);
    };

    inMemoryImage.onload = () => {
      this.imageCache[imageSrc] = {
        loaded: true,
        width: inMemoryImage.width,
        height: inMemoryImage.height
      };

      done();
    };

    inMemoryImage.src = imageSrc;
  }

  loadAllImages(props: any = this.props) {
    const generateLoadDoneCallback = (srcType: string, imageSrc: string) => (err: string) => {
      if (err) {
        return;
      }

      if (props[srcType] !== imageSrc || this.didUnmount) {
        return;
      }

      this.forceUpdate();
    };

    this.getSrcTypes().forEach(srcType => {
      const type = srcType.name;

      if ((props as any)[type] && this.state.loadErrorStatus[type]) {
        this.setState(prevState => ({
          loadErrorStatus: {...prevState.loadErrorStatus, [type]: false}
        }));
      }

      if ((props as any)[type] && !this.isImageLoaded((props as any)[type])) {
        this.loadImage(
            type,
            (props as any)[type],
            generateLoadDoneCallback(type, (props as any)[type])
        );
      }
    });
  }

  requestMove(direction: string, event: any) {
    const {onMoveNextRequest, onMovePrevRequest} = this.props;
    const nextState: any = {
      zoomLevel: MIN_ZOOM_LEVEL,
      offsetX: 0,
      offsetY: 0
    };

    if (!this.keyPressed) {
      nextState.shouldAnimate = true;
      this.setTimeout(
          () => this.setState({shouldAnimate: false}),
          300);
    }
    this.keyPressed = false;

    this.moveRequested = true;

    if (direction === 'prev') {
      this.keyCounter -= 1;
      this.setState(nextState);
      onMovePrevRequest(event);
    } else {
      this.keyCounter += 1;
      this.setState(nextState);
      onMoveNextRequest(event);
    }
  }

  requestMoveNext(event: any) {
    this.requestMove('next', event);
  }

  requestMovePrev(event: any) {
    this.requestMove('prev', event);
  }

  render() {

    const {
      imageTitle,
      nextSrc,
      prevSrc,
      segmentWidth,
      toolbarButtons,
      imageCaption,
      onCloseRequest,
      getMessage
    } = this.props;

    const {
      zoomLevel,
      offsetX,
      offsetY,
      loadErrorStatus
    } = this.state;

    const boxSize = this.getLightboxRect();
    let transitionStyle = {};

    const width = !_.isNil(segmentWidth) ? segmentWidth : getWindowWidth();
    if (this.isAnimating()) {
      transitionStyle = {
        ...transitionStyle,
        transition: `transform 300ms`
      };
    }

    const keyEndings: any = {};
    this.getSrcTypes().forEach(({name, keyEnding}) => {
      keyEndings[name] = keyEnding;
    });

    const images: any[] = [];
    const props: any = this.props;
    const addImage = (srcType: any, imageClass: any, transforms: any) => {
      if (!props[srcType]) {
        return;
      }
      const bestImageInfo = this.getBestImageForType(srcType);

      const imageStyle: any = {
        ...transitionStyle,
        ...ImageComponent.getTransform(width, {
          ...transforms,
          ...bestImageInfo
        })
      };

      if (zoomLevel > MIN_ZOOM_LEVEL) {
        imageStyle.cursor = 'move';
      }

      const hasTrueValue = (object: any) =>
          Object.keys(object).some(key => object[key]);

      if (bestImageInfo === null && hasTrueValue(loadErrorStatus)) {
        images.push(
            <div
                className={`${imageClass} ril__image`}
                style={imageStyle}
                key={props[srcType] + keyEndings[srcType]}
            >
              <div className="ril__errorContainer">
                {"This image failed to load"}
              </div>
            </div>
        );

        return;
      }
      if (bestImageInfo === null) {
        const loadingIcon = (
            <Loader key={props[srcType] + keyEndings[srcType]}
                    message={getMessage("loading")}
                    inverted
            />
        );

        images.push(loadingIcon);

        return;
      }

      const imageSrc = bestImageInfo.src;

      images.push(
          <div>
            <img
                className={`${imageClass} ril__image`}
                onDoubleClick={this.handleImageDoubleClick}
                onWheel={this.handleImageMouseWheel}
                onDragStart={e => e.preventDefault()}
                style={imageStyle}
                src={imageSrc}
                key={imageSrc + (keyEndings as any)[srcType]}
                alt={typeof imageTitle === "string" ? imageTitle : "Patient medical document image"}
                draggable={false}
            />
          </div>
      );
    };

    const zoomMultiplier = this.getZoomMultiplier();

    addImage('nextSrc', 'ril-image-next ril__imageNext', {
      x: 0
    });

    addImage('mainSrc', 'ril-image-current', {
      x: -1 * offsetX,
      y: -1 * offsetY,
      zoom: zoomMultiplier
    });

    addImage('prevSrc', 'ril-image-prev ril__imagePrev', {
      x: -1 * boxSize.width
    });

    return (
        <div
            className={`ril__outer ril__outerAnimating`}
            style={{
              transition: `opacity 300ms`,
              animationDuration: `300ms`,
              animationDirection: 'reverse'
            }}
            ref={this.outerEl}
            onWheel={this.handleOuterMousewheel}
            onMouseMove={this.handleMouseMove}
            onMouseDown={this.handleMouseDown}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            tabIndex={-1}
            onKeyDown={this.handleKeyInput}
            onKeyUp={this.handleKeyInput}
        >
          <div
              className="ril__inner"
              style={{overflow: 'auto'}}
          >
            {images}
          </div>

          {prevSrc && (
              <Button
                  icon
                  type="button"
                  style={{...navigateButtonStyle, left: 0, paddingTop: "20px"}}
                  key="prev"
                  aria-label={"Previous image page"}
                  title={"Previous image page"}
                  onClick={!this.isAnimating() ? this.requestMovePrev : undefined}
              >
                <Icon
                    name="chevron left"
                    size="big"
                    color="grey"
                />
              </Button>
          )}

          {nextSrc && (
              <Button
                  type="button"
                  icon
                  style={{...navigateButtonStyle, right: "0", padding: "40px 45px"}}
                  key="next"
                  aria-label={"Next image page"}
                  title={"Next image page"}
                  onClick={!this.isAnimating() ? this.requestMoveNext : undefined}
              >
                <Icon
                    name="chevron right"
                    size="big"
                    color="grey"
                />
              </Button>
          )}

          <div className="ril__toolbar">
            <ul className="ril__toolbarSide ril__toolbarLeftSide">
              <li className="ril__toolbarItem">
                <span className="ril__toolbarItemChild">
                  {imageTitle}
                </span>
              </li>
            </ul>

            <ul className="ril__toolbarSide ril__toolbarRightSide">
              {toolbarButtons &&
                  toolbarButtons.map((button, i) => (
                      <li
                          key={`button_${i + 1}`}
                          className="ril__toolbarItem"
                      >
                        {button}
                      </li>
                  ))}


              <li className="ril-toolbar__item ril__toolbarItem">
                <StyledZoomOptionsButton
                    type="button"
                    icon
                    inverted
                    key="zoom-in"
                    aria-label={"Zoom in"}
                    title={"Zoom in"}
                    className={[
                      'styled-button',
                      ...(zoomLevel === MAX_ZOOM_LEVEL
                          ? ['ril__builtinButtonDisabled']
                          : [])
                    ].join(' ')}
                    ref={this.zoomInBtn}
                    disabled={
                        this.isAnimating() || zoomLevel === MAX_ZOOM_LEVEL
                    }
                    onClick={
                      !this.isAnimating() && zoomLevel !== MAX_ZOOM_LEVEL
                          ? this.handleZoomInButtonClick
                          : undefined
                    }
                >
                  <Icon name="zoom-in" size="large"/>
                </StyledZoomOptionsButton>
              </li>

              <li className="ril__toolbarItem">
                <StyledZoomOptionsButton
                    type="button"
                    icon
                    inverted
                    key="default-view"
                    aria-label={"Default view"}
                    title={"Default view"}
                    className={[
                      'styled-button',
                      ...(zoomLevel === MIN_ZOOM_LEVEL
                          ? ['ril__builtinButtonDisabled']
                          : [])
                    ].join(' ')}
                    ref={this.defaultImgView}
                    disabled={
                        this.isAnimating() || zoomLevel === MIN_ZOOM_LEVEL
                    }
                    onClick={
                      !this.isAnimating() && zoomLevel !== MIN_ZOOM_LEVEL
                          ? this.handleDefaultViewButtonClick
                          : undefined
                    }
                >
                  <Icon name="search" size="large"/>
                </StyledZoomOptionsButton>
              </li>

              <li className="ril__toolbarItem">
                <StyledZoomOptionsButton
                    type="button"
                    icon
                    inverted
                    key="zoom-out"
                    aria-label={"Zoom out"}
                    title={"Zoom out"}
                    className={[
                      'styled-button',
                      ...(zoomLevel === MIN_ZOOM_LEVEL
                          ? ['ril__builtinButtonDisabled']
                          : [])
                    ].join(' ')}
                    ref={this.zoomOutBtn}
                    disabled={
                        this.isAnimating() || zoomLevel === MIN_ZOOM_LEVEL
                    }
                    onClick={
                      !this.isAnimating() && zoomLevel !== MIN_ZOOM_LEVEL
                          ? this.handleZoomOutButtonClick
                          : undefined
                    }
                >
                  <Icon name="zoom-out" size="large"/>
                </StyledZoomOptionsButton>
              </li>
              {
                  onCloseRequest !== undefined &&
                  <li className="ril__toolbarItem">
                    <StyledZoomOptionsButton
                        type="button"
                        icon
                        inverted
                        key="close"
                        aria-label={"Close"}
                        title={"Close"}
                        className={[
                          'styled-button']}
                        onClick={!this.isAnimating() ? onCloseRequest : undefined}
                    >
                      <Icon name="close" size="large"/>
                    </StyledZoomOptionsButton>
                  </li>
              }
            </ul>
          </div>

          {imageCaption && (
              <div
                  onWheel={this.handleCaptionMousewheel}
                  onMouseDown={event => event.stopPropagation()}
                  className="ril__caption"
                  ref={this.caption}
              >
                <div className="ril__captionContent">
                  {imageCaption}
                </div>
              </div>
          )}
        </div>
    );
  }

}

export default withMessages(ImageComponent);
